import { usePageContent } from "@/hooks/usePageContent";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/base/ScrollReveal";

const cases = [
  {
    tag: "Hospital",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Undeveloped Plot to",
    highlight: "Healthcare Hub",
    location: "Mysuru, Karnataka",
    desc: "3-acre idle highway plot transformed into a 150-bed multi-specialty hospital serving five districts, addressing a severe doctor-density deficit identified by AI catchment analysis.",
    before: ["Idle highway-adjacent plot", "Zero community infrastructure", "No medical facilities within 25 km"],
    afterTitle: "After 24 Months",
    afterMetrics: [
      { label: "Bed Capacity", value: "150" },
      { label: "Specialty Wings", value: "8" },
      { label: "Daily Outpatients", value: "350+" },
    ],
    image: "https://readdy.ai/api/search-image?query=Modern%20multi-specialty%20hospital%20building%20in%20India%20at%20golden%20hour%2C%20clean%20white%20facade%20with%20glass%20accents%2C%20landscaped%20entrance%2C%20warm%20natural%20lighting%2C%20editorial%20architectural%20photography%2C%20neutral%20sky%20background&width=800&height=600&seq=home-case-hospital-01&orientation=landscape",
  },
  {
    tag: "Warehouse",
    tagColor: "bg-secondary-500/10 text-secondary-700",
    title: "Agricultural Land to",
    highlight: "Logistics Hub",
    location: "Hosur, Tamil Nadu",
    desc: "8 acres of marginal crop land near the Bengaluru-Chennai corridor converted into a 150,000 sq.ft Grade-A distribution center serving six 3PL partners.",
    before: ["Marginal seasonal crop land", "No warehousing within 20 km", "Seasonal income only"],
    afterTitle: "After 12 Months",
    afterMetrics: [
      { label: "Warehouse Area", value: "150K sq.ft" },
      { label: "Daily Dispatch", value: "12K Units" },
      { label: "3PL Partners", value: "6" },
    ],
    image: "https://readdy.ai/api/search-image?query=Modern%20logistics%20warehouse%20distribution%20center%20in%20India%20with%20trucks%20loading%20at%20docks%2C%20large%20industrial%20building%2C%20organized%20pallet%20racking%2C%20blue%20sky%2C%20editorial%20industrial%20photography%2C%20clean%20professional%20look&width=800&height=600&seq=home-case-warehouse-02&orientation=landscape",
  },
  {
    tag: "Co-living",
    tagColor: "bg-primary-500/10 text-primary-700",
    title: "Old Block to",
    highlight: "Co-living Community",
    location: "Kondapur, Hyderabad",
    desc: "3 acres of crumbling residential structures redeveloped into a 300-bed professional co-living community with 96% occupancy and a 200-person waitlist.",
    before: ["Crumbling residential structures", "Unsafe living conditions", "No community amenities"],
    afterTitle: "After 16 Months",
    afterMetrics: [
      { label: "Bed Capacity", value: "300" },
      { label: "Occupancy Rate", value: "96%" },
      { label: "Resident Satisfaction", value: "4.6/5" },
    ],
    image: "https://readdy.ai/api/search-image?query=Modern%20co-living%20residential%20community%20in%20India%2C%20bright%20contemporary%20building%20with%20balconies%20and%20shared%20terrace%2C%20young%20professionals%20socializing%20in%20courtyard%2C%20green%20landscaping%2C%20warm%20daylight%2C%20editorial%20lifestyle%20photography&width=800&height=600&seq=home-case-coliving-03&orientation=landscape",
  },
];

export default function CaseStudiesSection() {
  const { getContent } = usePageContent('homepage');
  const heading = getContent('home-case-studies-heading') || 'Real Transformations. Real Results.';

  return (
    <section id="case-studies" className="py-24 bg-background-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
            Success Stories
          </span>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {heading}
          </h2>
          <p className="text-foreground-600">
            See how idle land becomes thriving infrastructure using our AI-backed feasibility analysis across 12 proven use-cases.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {cases.map((c, i) => (
            <ScrollReveal key={c.title} delay={i * 80}>
            <article className="rounded-2xl card-dark overflow-hidden flex flex-col h-full">
              {/* Image */}
              <div className="aspect-[4/3] overflow-hidden shrink-0">
                <img
                  src={c.image}
                  alt={`${c.title} ${c.highlight}`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                />
              </div>
              {/* Content */}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${c.tagColor}`}>
                    {c.tag}
                  </span>
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-map-pin-line" />{c.location}
                  </span>
                </div>
                <h3 className="text-lg md:text-xl font-display font-bold text-foreground-950 mb-2 leading-snug">
                  {c.title}<br />
                  <span className="gradient-text-accent">{c.highlight}</span>
                </h3>
                <p className="text-sm text-foreground-600 mb-4 leading-relaxed flex-1">
                  {c.desc}
                </p>

                {/* Before / After */}
                <div className="flex flex-col gap-4 mb-4">
                  <div className="p-4 rounded-xl bg-background-300/30 border border-background-400/20">
                    <span className="text-xs font-semibold text-foreground-500 uppercase tracking-wider block mb-2.5">Before</span>
                    <ul className="space-y-2">
                      {c.before.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs text-foreground-500 leading-relaxed">
                          <i className="ri-close-circle-line text-foreground-600 shrink-0 mt-0.5" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-4 rounded-xl bg-primary-500/5 border border-primary-800/30">
                    <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider block mb-2.5">{c.afterTitle}</span>
                    <div className="space-y-2">
                      {c.afterMetrics.map((m) => (
                        <div key={m.label} className="text-xs">
                          <span className="text-foreground-950 font-semibold">{m.value}</span>
                          <span className="text-foreground-500 ml-1.5">{m.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <Link
                  to="/case-studies"
                  className="mt-auto inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm"
                >
                  View All 12 Case Studies
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </article>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}